import React, { useMemo, useState } from "react";
import {
  createFinanceExpense,
  createFinanceLiability,
  getApiErrorMessage,
  patchFinanceExpense,
  patchFinanceLiability,
  putFinanceExpensePayout,
  putFinanceLiabilityPayout,
} from "../../api";
import ErrorRetry from "../../components/ErrorRetry";
import Loading from "../../components/Loading";
import FinanceTabs from "./FinanceTabs";
import {
  LIABILITY_OWNERS,
  RECURRENCES,
  SPHERES,
  ownerLabel,
  parseAmount,
  pctInputToRate,
  rateToPctInput,
  sphereLabel,
} from "./financeUtils";
import useFinanceOverview from "./useFinanceOverview";

const EMPTY_LIABILITY = {
  title: "",
  owner: "lina",
  sphere: "personal",
  payment_day: "1",
  payment_amount: "",
  debt_remaining: "",
  interest_rate: "",
};

function compareAccounts(a, b) {
  return (Number(a.sort_order) - Number(b.sort_order)) || (Number(a.id) - Number(b.id));
}

function visibleAccounts(accounts) {
  return [...(accounts || [])]
    .filter((item) => !item.archived)
    .sort(compareAccounts);
}

function draftValue(drafts, id, fallback) {
  return Object.prototype.hasOwnProperty.call(drafts, id) ? drafts[id] : fallback;
}

function innDraftValue(drafts, id, stored) {
  return draftValue(drafts, id, stored || "");
}

function isInnEmpty(value) {
  return !String(value).trim();
}

function parseDay(raw) {
  const day = Number.parseInt(String(raw ?? ""), 10);
  if (!Number.isInteger(day) || day < 1 || day > 31) return { ok: false };
  return { ok: true, value: day };
}

function emptyExpenseForm(month, paidFromId) {
  return {
    title: "",
    amount: "",
    paid_from_account_id: paidFromId == null ? "" : String(paidFromId),
    recurrence: "monthly",
    day: "1",
    start_date: `${month}-01`,
    end_date: "",
    sphere: "business",
    comment: "",
  };
}

export default function FinancePayments() {
  const { month, setMonth, overview, loading, error, reload } = useFinanceOverview();
  const [loanDayDrafts, setLoanDayDrafts] = useState({});
  const [loanPaymentDrafts, setLoanPaymentDrafts] = useState({});
  const [loanDebtDrafts, setLoanDebtDrafts] = useState({});
  const [loanRateDrafts, setLoanRateDrafts] = useState({});
  const [loanInnDrafts, setLoanInnDrafts] = useState({});
  const [expenseAmountDrafts, setExpenseAmountDrafts] = useState({});
  const [expenseDayDrafts, setExpenseDayDrafts] = useState({});
  const [expenseStartDrafts, setExpenseStartDrafts] = useState({});
  const [expenseEndDrafts, setExpenseEndDrafts] = useState({});
  const [expenseInnDrafts, setExpenseInnDrafts] = useState({});
  const [showLiabilityForm, setShowLiabilityForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [liabilityForm, setLiabilityForm] = useState(EMPTY_LIABILITY);
  const [expenseForm, setExpenseForm] = useState(() => emptyExpenseForm(month, ""));
  const [saveNotice, setSaveNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const accounts = useMemo(() => visibleAccounts(overview?.accounts), [overview]);
  const liabilities = overview?.liabilities || [];
  const expenses = overview?.expenses || [];
  const activeLoans = liabilities.filter((loan) => loan.is_active !== false);
  const inactiveLoans = liabilities.filter((loan) => loan.is_active === false);
  const activeExpenses = expenses.filter((row) => row.is_active !== false);
  const inactiveExpenses = expenses.filter((row) => row.is_active === false);

  const resetNotices = () => {
    setSaveNotice("");
    setActionError("");
  };

  const afterSave = async () => {
    setSaveNotice("Сохранено");
    await reload();
  };

  const failSave = (err) => {
    setSaveNotice("");
    setActionError(getApiErrorMessage(err, "Не удалось сохранить"));
  };

  const runSave = async (action) => {
    resetNotices();
    try {
      await action();
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const patchLoan = (loan, field, next, current) => {
    if (next === current) return;
    return runSave(() => patchFinanceLiability(loan.id, { [field]: next }));
  };

  const saveLoanDay = (loan) => {
    const parsed = parseDay(draftValue(loanDayDrafts, loan.id, loan.payment_day));
    if (!parsed.ok) return;
    return patchLoan(loan, "payment_day", parsed.value, Number(loan.payment_day));
  };

  const saveLoanPayment = (loan) => {
    const parsed = parseAmount(draftValue(
      loanPaymentDrafts,
      loan.id,
      loan.payment_amount == null ? "" : String(loan.payment_amount)
    ));
    if (!parsed.ok) return;
    return patchLoan(loan, "payment_amount", parsed.value, Number(loan.payment_amount));
  };

  const saveLoanDebt = (loan) => {
    const parsed = parseAmount(draftValue(
      loanDebtDrafts,
      loan.id,
      loan.debt_remaining == null ? "" : String(loan.debt_remaining)
    ));
    if (!parsed.ok) return;
    return patchLoan(loan, "debt_remaining", parsed.value, Number(loan.debt_remaining));
  };

  const saveLoanRate = (loan) => {
    const raw = draftValue(loanRateDrafts, loan.id, rateToPctInput(loan.interest_rate));
    if (raw == null || String(raw).trim() === "") {
      if (loan.interest_rate == null) return;
      return runSave(() => patchFinanceLiability(loan.id, { interest_rate: null }));
    }
    const parsed = pctInputToRate(raw);
    if (!parsed.ok) return;
    return patchLoan(loan, "interest_rate", parsed.value, Number(loan.interest_rate));
  };

  const saveLoanPaidFrom = (loan, raw) => {
    const next = raw === "" ? null : Number(raw);
    return runSave(() => patchFinanceLiability(loan.id, { paid_from_account_id: next }));
  };

  const saveLoanInn = (loan) => {
    const raw = String(innDraftValue(loanInnDrafts, loan.id, loan.counterparty_inn)).trim();
    const next = raw || null;
    const current = loan.counterparty_inn || null;
    return patchLoan(loan, "counterparty_inn", next, current);
  };

  const confirmUnsetBankPayout = (paidSource) => {
    if (paidSource !== "bank") return true;
    return window.confirm(
      "Снять отметку, поставленную банком? Платёж вернётся в «Не разобрано»."
    );
  };

  const toggleLoanPaid = (loan, paid) => {
    if (!paid && !confirmUnsetBankPayout(loan.paid_source)) return;
    return runSave(() => putFinanceLiabilityPayout(loan.id, { month, paid }));
  };

  const deactivateLoan = (loan) => {
    if (!window.confirm(`Выключить кредит «${loan.title}»?`)) return;
    return runSave(() => patchFinanceLiability(loan.id, { is_active: false }));
  };

  const activateLoan = (loan) => {
    return runSave(() => patchFinanceLiability(loan.id, { is_active: true }));
  };

  const handleCreateLiability = async (event) => {
    event.preventDefault();
    const title = liabilityForm.title.trim();
    if (!title) return;
    const day = parseDay(liabilityForm.payment_day);
    const amount = parseAmount(liabilityForm.payment_amount);
    const debt = parseAmount(liabilityForm.debt_remaining);
    if (!day.ok || !amount.ok || amount.value < 0 || !debt.ok || debt.value < 0) return;
    const payload = {
      title,
      owner: liabilityForm.owner,
      sphere: liabilityForm.sphere,
      payment_day: day.value,
      payment_amount: amount.value,
      debt_remaining: debt.value,
    };
    const rateRaw = String(liabilityForm.interest_rate ?? "").trim();
    if (rateRaw) {
      const rate = pctInputToRate(rateRaw);
      if (!rate.ok) return;
      payload.interest_rate = rate.value;
    }
    resetNotices();
    try {
      await createFinanceLiability(payload);
      setShowLiabilityForm(false);
      setLiabilityForm(EMPTY_LIABILITY);
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const patchExpense = (row, field, next, current) => {
    if (next === current) return;
    return runSave(() => patchFinanceExpense(row.id, { [field]: next }));
  };

  const saveExpenseAmount = (row) => {
    const parsed = parseAmount(draftValue(
      expenseAmountDrafts,
      row.id,
      row.amount == null ? "" : String(row.amount)
    ));
    if (!parsed.ok) return;
    return patchExpense(row, "amount", parsed.value, Number(row.amount));
  };

  const saveExpenseDay = (row) => {
    const parsed = parseDay(draftValue(expenseDayDrafts, row.id, row.day));
    if (!parsed.ok) return;
    return patchExpense(row, "day", parsed.value, Number(row.day));
  };

  const saveExpenseStart = (row) => {
    const next = String(draftValue(expenseStartDrafts, row.id, row.start_date || "")).trim();
    if (!next) return;
    return patchExpense(row, "start_date", next, row.start_date || null);
  };

  const saveExpenseEnd = (row) => {
    const raw = String(draftValue(expenseEndDrafts, row.id, row.end_date || "")).trim();
    const next = raw || null;
    return patchExpense(row, "end_date", next, row.end_date || null);
  };

  const saveExpenseSelect = (row, field, raw, asNumber = false) => {
    const next = asNumber ? Number(raw) : raw;
    const current = asNumber ? Number(row[field]) : row[field];
    return patchExpense(row, field, next, current);
  };

  const saveExpenseInn = (row) => {
    const raw = String(innDraftValue(expenseInnDrafts, row.id, row.counterparty_inn)).trim();
    const next = raw || null;
    const current = row.counterparty_inn || null;
    return patchExpense(row, "counterparty_inn", next, current);
  };

  const toggleExpensePaid = (row, paid) => {
    if (!paid && !confirmUnsetBankPayout(row.paid_source)) return;
    return runSave(() => putFinanceExpensePayout(row.id, { month, paid }));
  };

  const deactivateExpense = (row) => {
    if (!window.confirm(`Выключить расход «${row.title}»?`)) return;
    return runSave(() => patchFinanceExpense(row.id, { is_active: false }));
  };

  const activateExpense = (row) => {
    return runSave(() => patchFinanceExpense(row.id, { is_active: true }));
  };

  const toggleExpenseForm = () => {
    setShowExpenseForm((open) => {
      if (open) return false;
      const first = accounts[0];
      setExpenseForm(emptyExpenseForm(month, first?.id));
      return true;
    });
  };

  const handleCreateExpense = async (event) => {
    event.preventDefault();
    const title = expenseForm.title.trim();
    if (!title) return;
    const amount = parseAmount(expenseForm.amount);
    const day = parseDay(expenseForm.day);
    const paidFrom = Number(expenseForm.paid_from_account_id);
    const startDate = String(expenseForm.start_date || "").trim();
    if (!amount.ok || amount.value <= 0 || !day.ok || !paidFrom || !startDate) return;
    const comment = String(expenseForm.comment || "").trim();
    const endDate = String(expenseForm.end_date || "").trim();
    resetNotices();
    try {
      await createFinanceExpense({
        title,
        amount: amount.value,
        paid_from_account_id: paidFrom,
        recurrence: expenseForm.recurrence,
        day: day.value,
        start_date: startDate,
        end_date: endDate || null,
        sphere: expenseForm.sphere,
        comment: comment || null,
      });
      setShowExpenseForm(false);
      setExpenseForm(emptyExpenseForm(month, accounts[0]?.id));
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const accountOptions = accounts.map((account) => (
    <option key={account.id} value={account.id}>{account.title}</option>
  ));

  return (
    <div className="page-container fin-page">
      <FinanceTabs month={month} onMonthChange={setMonth} title="Платежи" />
      {error && <ErrorRetry message={error} onRetry={() => reload()} />}
      {saveNotice && (
        <div className="fin-save-ok" role="status">{saveNotice}</div>
      )}
      {actionError && (
        <div className="login-error" role="status" aria-live="polite">{actionError}</div>
      )}
      {loading && !overview ? (
        <Loading text="Загрузка кассы…" />
      ) : overview ? (
        <>
          <section className="fin-section fin-panel" aria-label="Кредиты">
            <h3>Кредиты</h3>
            <div className="fin-table-wrap">
              <table className="fin-payments">
              <caption>Кредиты</caption>
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Владелец</th>
                  <th>Сфера</th>
                  <th>День</th>
                  <th>Платёж</th>
                  <th>Долг</th>
                  <th>Ставка</th>
                  <th>Откуда платим</th>
                  <th>ИНН получателя</th>
                  <th>Оплачен</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {activeLoans.map((loan) => (
                  <tr key={loan.id}>
                    <td>{loan.title}</td>
                    <td>{ownerLabel(loan.owner)}</td>
                    <td>{sphereLabel(loan.sphere)}</td>
                    <td>
                      <label className="login-label">
                        День {loan.title}
                        <input
                          className="login-input"
                          type="number"
                          min="1"
                          max="31"
                          value={draftValue(loanDayDrafts, loan.id, loan.payment_day ?? "")}
                          onChange={(e) => {
                            setLoanDayDrafts((prev) => ({ ...prev, [loan.id]: e.target.value }));
                          }}
                          onBlur={() => saveLoanDay(loan)}
                        />
                      </label>
                    </td>
                    <td>
                      <label className="login-label">
                        Платёж {loan.title}
                        <input
                          className="login-input"
                          value={draftValue(
                            loanPaymentDrafts,
                            loan.id,
                            loan.payment_amount == null ? "" : String(loan.payment_amount)
                          )}
                          onChange={(e) => {
                            setLoanPaymentDrafts((prev) => ({ ...prev, [loan.id]: e.target.value }));
                          }}
                          onBlur={() => saveLoanPayment(loan)}
                        />
                      </label>
                    </td>
                    <td>
                      <label className="login-label">
                        Долг {loan.title}
                        <input
                          className="login-input"
                          value={draftValue(
                            loanDebtDrafts,
                            loan.id,
                            loan.debt_remaining == null ? "" : String(loan.debt_remaining)
                          )}
                          onChange={(e) => {
                            setLoanDebtDrafts((prev) => ({ ...prev, [loan.id]: e.target.value }));
                          }}
                          onBlur={() => saveLoanDebt(loan)}
                        />
                      </label>
                    </td>
                    <td>
                      <label className="login-label">
                        Ставка {loan.title}
                        <input
                          className="login-input"
                          value={draftValue(
                            loanRateDrafts,
                            loan.id,
                            rateToPctInput(loan.interest_rate)
                          )}
                          onChange={(e) => {
                            setLoanRateDrafts((prev) => ({ ...prev, [loan.id]: e.target.value }));
                          }}
                          onBlur={() => saveLoanRate(loan)}
                        />
                      </label>
                    </td>
                    <td>
                      <select
                        className="login-input"
                        aria-label={`Откуда ${loan.title}`}
                        value={loan.paid_from_account_id == null ? "" : String(loan.paid_from_account_id)}
                        onChange={(e) => saveLoanPaidFrom(loan, e.target.value)}
                      >
                        <option value="">Фонд Кредит</option>
                        {accountOptions}
                      </select>
                    </td>
                    <td>
                      <input
                        className="login-input"
                        aria-label={`ИНН получателя ${loan.title}`}
                        title={
                          isInnEmpty(innDraftValue(loanInnDrafts, loan.id, loan.counterparty_inn))
                            ? "Автоотметка выключена"
                            : undefined
                        }
                        value={innDraftValue(loanInnDrafts, loan.id, loan.counterparty_inn)}
                        onChange={(e) => {
                          setLoanInnDrafts((prev) => ({ ...prev, [loan.id]: e.target.value }));
                        }}
                        onBlur={() => saveLoanInn(loan)}
                      />
                      {isInnEmpty(innDraftValue(loanInnDrafts, loan.id, loan.counterparty_inn)) && (
                        <span className="fin-inn-hint">нет автоотметки</span>
                      )}
                    </td>
                    <td>
                      <label className="fin-check">
                        <input
                          type="checkbox"
                          checked={Boolean(loan.paid_this_month)}
                          onChange={(e) => toggleLoanPaid(loan, e.target.checked)}
                          aria-label={`Оплачен ${loan.title}`}
                        />
                        Оплачен
                        {loan.paid_source === "bank" && (
                          <span className="fin-bank-badge" title="Отмечено банком">банк</span>
                        )}
                      </label>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => deactivateLoan(loan)}
                        aria-label={`Выключить ${loan.title}`}
                      >
                        Выключить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {inactiveLoans.length > 0 && (
              <details className="fin-inactive">
                <summary>Выключенные</summary>
                <ul className="fin-inactive-list">
                  {inactiveLoans.map((loan) => (
                    <li key={loan.id} className="fin-inactive-row">
                      <span>{loan.title}</span>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => activateLoan(loan)}
                      >
                        Включить
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowLiabilityForm((open) => !open)}
            >
              Добавить кредит
            </button>
            {showLiabilityForm && (
              <form className="fin-inline-form fin-form-card" onSubmit={handleCreateLiability}>
                <label className="login-label">
                  Название кредита
                  <input
                    className="login-input"
                    value={liabilityForm.title}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, title: e.target.value })}
                    required
                  />
                </label>
                <label className="login-label">
                  Владелец кредита
                  <select
                    className="login-input"
                    value={liabilityForm.owner}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, owner: e.target.value })}
                  >
                    {LIABILITY_OWNERS.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </label>
                <label className="login-label">
                  Сфера
                  <select
                    className="login-input"
                    value={liabilityForm.sphere}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, sphere: e.target.value })}
                  >
                    {SPHERES.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </label>
                <label className="login-label">
                  День платежа
                  <input
                    className="login-input"
                    type="number"
                    min="1"
                    max="31"
                    value={liabilityForm.payment_day}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, payment_day: e.target.value })}
                  />
                </label>
                <label className="login-label">
                  Платёж
                  <input
                    className="login-input"
                    value={liabilityForm.payment_amount}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, payment_amount: e.target.value })}
                  />
                </label>
                <label className="login-label">
                  Остаток долга
                  <input
                    className="login-input"
                    value={liabilityForm.debt_remaining}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, debt_remaining: e.target.value })}
                  />
                </label>
                <label className="login-label">
                  Ставка кредита
                  <input
                    className="login-input"
                    value={liabilityForm.interest_rate}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, interest_rate: e.target.value })}
                  />
                </label>
                <button type="submit" className="btn">Создать кредит</button>
              </form>
            )}
          </section>

          <section className="fin-section fin-panel" aria-label="Расходы">
            <h3>Расходы</h3>
            <div className="fin-table-wrap">
              <table className="fin-payments">
              <caption>Расходы</caption>
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Сумма</th>
                  <th>Периодичность</th>
                  <th>День</th>
                  <th>Дата начала</th>
                  <th>Дата окончания</th>
                  <th>Откуда платим</th>
                  <th>Сфера</th>
                  <th>ИНН получателя</th>
                  <th>Оплачен</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {activeExpenses.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title}</td>
                    <td>
                      <input
                        className="login-input"
                        aria-label={`Сумма ${row.title}`}
                        value={draftValue(
                          expenseAmountDrafts,
                          row.id,
                          row.amount == null ? "" : String(row.amount)
                        )}
                        onChange={(e) => {
                          setExpenseAmountDrafts((prev) => ({ ...prev, [row.id]: e.target.value }));
                        }}
                        onBlur={() => saveExpenseAmount(row)}
                      />
                    </td>
                    <td>
                      <select
                        className="login-input"
                        aria-label={`Периодичность ${row.title}`}
                        value={row.recurrence}
                        onChange={(e) => saveExpenseSelect(row, "recurrence", e.target.value)}
                      >
                        {RECURRENCES.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="login-input"
                        type="number"
                        min="1"
                        max="31"
                        aria-label={`День ${row.title}`}
                        value={draftValue(expenseDayDrafts, row.id, row.day ?? "")}
                        disabled={row.recurrence === "once"}
                        onChange={(e) => {
                          setExpenseDayDrafts((prev) => ({ ...prev, [row.id]: e.target.value }));
                        }}
                        onBlur={() => saveExpenseDay(row)}
                      />
                    </td>
                    <td>
                      <input
                        className="login-input"
                        type="date"
                        aria-label={`Начало ${row.title}`}
                        value={draftValue(expenseStartDrafts, row.id, row.start_date || "")}
                        onChange={(e) => {
                          setExpenseStartDrafts((prev) => ({ ...prev, [row.id]: e.target.value }));
                        }}
                        onBlur={() => saveExpenseStart(row)}
                      />
                    </td>
                    <td>
                      <input
                        className="login-input"
                        type="date"
                        aria-label={`Окончание ${row.title}`}
                        value={draftValue(expenseEndDrafts, row.id, row.end_date || "")}
                        onChange={(e) => {
                          setExpenseEndDrafts((prev) => ({ ...prev, [row.id]: e.target.value }));
                        }}
                        onBlur={() => saveExpenseEnd(row)}
                      />
                    </td>
                    <td>
                      <select
                        className="login-input"
                        aria-label={`Откуда ${row.title}`}
                        value={row.paid_from_account_id == null ? "" : String(row.paid_from_account_id)}
                        onChange={(e) => saveExpenseSelect(row, "paid_from_account_id", e.target.value, true)}
                      >
                        {accountOptions}
                      </select>
                    </td>
                    <td>
                      <select
                        className="login-input"
                        aria-label={`Сфера ${row.title}`}
                        value={row.sphere}
                        onChange={(e) => saveExpenseSelect(row, "sphere", e.target.value)}
                      >
                        {SPHERES.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="login-input"
                        aria-label={`ИНН получателя ${row.title}`}
                        title={
                          isInnEmpty(innDraftValue(expenseInnDrafts, row.id, row.counterparty_inn))
                            ? "Автоотметка выключена"
                            : undefined
                        }
                        value={innDraftValue(expenseInnDrafts, row.id, row.counterparty_inn)}
                        onChange={(e) => {
                          setExpenseInnDrafts((prev) => ({ ...prev, [row.id]: e.target.value }));
                        }}
                        onBlur={() => saveExpenseInn(row)}
                      />
                      {isInnEmpty(innDraftValue(expenseInnDrafts, row.id, row.counterparty_inn)) && (
                        <span className="fin-inn-hint">нет автоотметки</span>
                      )}
                    </td>
                    <td>
                      <label className="fin-check">
                        <input
                          type="checkbox"
                          checked={Boolean(row.paid_this_month)}
                          onChange={(e) => toggleExpensePaid(row, e.target.checked)}
                          aria-label={`Оплачен ${row.title}`}
                        />
                        Оплачен
                        {row.paid_source === "bank" && (
                          <span className="fin-bank-badge" title="Отмечено банком">банк</span>
                        )}
                      </label>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => deactivateExpense(row)}
                      >
                        Выключить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {inactiveExpenses.length > 0 && (
              <details className="fin-inactive">
                <summary>Выключенные</summary>
                <ul className="fin-inactive-list">
                  {inactiveExpenses.map((row) => (
                    <li key={row.id} className="fin-inactive-row">
                      <span>{row.title}</span>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => activateExpense(row)}
                      >
                        Включить
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={toggleExpenseForm}
            >
              Добавить расход
            </button>
            {showExpenseForm && (
              <form className="fin-inline-form fin-form-card" onSubmit={handleCreateExpense}>
                <label className="login-label">
                  Название
                  <input
                    className="login-input"
                    value={expenseForm.title}
                    onChange={(e) => setExpenseForm({ ...expenseForm, title: e.target.value })}
                    required
                  />
                </label>
                <label className="login-label">
                  Сумма
                  <input
                    className="login-input"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    required
                  />
                </label>
                <label className="login-label">
                  Периодичность
                  <select
                    className="login-input"
                    value={expenseForm.recurrence}
                    onChange={(e) => setExpenseForm({ ...expenseForm, recurrence: e.target.value })}
                  >
                    {RECURRENCES.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </label>
                {expenseForm.recurrence !== "once" && (
                  <label className="login-label">
                    День
                    <input
                      className="login-input"
                      type="number"
                      min="1"
                      max="31"
                      value={expenseForm.day}
                      onChange={(e) => setExpenseForm({ ...expenseForm, day: e.target.value })}
                    />
                  </label>
                )}
                <label className="login-label">
                  Дата начала
                  <input
                    className="login-input"
                    type="date"
                    value={expenseForm.start_date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, start_date: e.target.value })}
                    required
                  />
                </label>
                <label className="login-label">
                  Дата окончания
                  <input
                    className="login-input"
                    type="date"
                    value={expenseForm.end_date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, end_date: e.target.value })}
                  />
                </label>
                <label className="login-label">
                  Откуда платим
                  <select
                    className="login-input"
                    value={expenseForm.paid_from_account_id}
                    onChange={(e) => setExpenseForm({ ...expenseForm, paid_from_account_id: e.target.value })}
                    required
                  >
                    {accountOptions}
                  </select>
                </label>
                <label className="login-label">
                  Сфера
                  <select
                    className="login-input"
                    value={expenseForm.sphere}
                    onChange={(e) => setExpenseForm({ ...expenseForm, sphere: e.target.value })}
                  >
                    {SPHERES.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </label>
                <label className="login-label">
                  Комментарий
                  <input
                    className="login-input"
                    value={expenseForm.comment}
                    onChange={(e) => setExpenseForm({ ...expenseForm, comment: e.target.value })}
                  />
                </label>
                <button type="submit" className="btn">Создать</button>
              </form>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
